import { useEffect, useState } from "react";
import { canInstall, isInstalled, isIOS, isIOSSafari, promptInstall } from "@/lib/pwaInstall";
import { Download, Share, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// INSTALL HUSSAR TREASURY — platform-correct install affordance.
// Android/Chromium: native prompt via captured beforeinstallprompt.
// iOS: honest Share → Add to Home Screen instructions (no fake auto-install).
// Installed: restrained APP INSTALLED status.
export function InstallAppButton({ className = "" }) {
  const [installable, setInstallable] = useState(canInstall());
  const [installed, setInstalled] = useState(isInstalled());
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener("hussar-pwa-installable", onInstallable);
    window.addEventListener("hussar-pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("hussar-pwa-installable", onInstallable);
      window.removeEventListener("hussar-pwa-installed", onInstalled);
    };
  }, []);

  if (installed) {
    return (
      <span data-testid="app-installed-status"
        className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-widest text-emerald-400 border border-emerald-700/50 bg-emerald-950/30 rounded-sm px-3 py-2 ${className}`}>
        <BadgeCheck className="w-3.5 h-3.5" /> APP INSTALLED
      </span>
    );
  }

  if (installable) {
    return (
      <Button data-testid="install-app-button"
        onClick={async () => { if (await promptInstall()) setInstalled(true); }}
        className={`bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold tracking-wide ${className}`}>
        <Download className="w-4 h-4 mr-2" /> INSTALL HUSSAR TREASURY
      </Button>
    );
  }

  if (isIOS()) {
    return (
      <>
        <Button data-testid="install-app-ios-button" variant="outline" onClick={() => setShowIOS(true)}
          className={`border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 ${className}`}>
          <Share className="w-4 h-4 mr-2" /> INSTALL HUSSAR TREASURY
        </Button>
        <Dialog open={showIOS} onOpenChange={setShowIOS}>
          <DialogContent className="bg-[#17191E] border-[#2D323E] text-slate-100 max-w-sm" data-testid="ios-install-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-wide">Install Hussar Treasury</DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                {isIOSSafari()
                  ? "iOS installs apps from Safari's Share menu:"
                  : "On iPhone/iPad, installation must be done from Safari (open this page in Safari first):"}
              </DialogDescription>
            </DialogHeader>
            <ol className="space-y-3 text-sm text-slate-200 mt-2">
              <li className="flex items-center gap-3" data-testid="ios-step-1">
                <span className="w-6 h-6 rounded-sm bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">1</span>
                Tap <Share className="w-4 h-4 inline text-slate-400 mx-1" /> <b>Share</b> (bottom toolbar)
              </li>
              <li className="flex items-center gap-3" data-testid="ios-step-2">
                <span className="w-6 h-6 rounded-sm bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">2</span>
                Tap <b>Add to Home Screen</b>
              </li>
              <li className="flex items-center gap-3" data-testid="ios-step-3">
                <span className="w-6 h-6 rounded-sm bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">3</span>
                Tap <b>Add</b>
              </li>
            </ol>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <span data-testid="install-unavailable-note" className={`text-[11px] text-slate-400 ${className}`}>
      Installable as an app (PWA) — use your browser menu → "Install app" / "Add to Home Screen".
    </span>
  );
}
